/**
 * Non-blocking advice about an assessment document.
 *
 * The AJV schema in `./schema` is a statement about validity: everything it
 * reports blocks. Soft advice lives here instead so the schema does not have to
 * grow a severity concept, and so a document can be imperfect but still
 * publishable. Entries use the same `{ group, groupElement, message }` shape
 * that `validators.getObjectGroup` produces, which lets `EditorErrors` group a
 * warning onto the same card as that element's errors.
 */

import { MAX_SENTIMENT_OPTIONS } from 'js/utils/sentiment-question';

export const WARNING_SEVERITY = 'warning';

function warn(groupElement, message, group = 'element') {
  return { group, groupElement, message, severity: WARNING_SEVERITY };
}

function questionElements(assessment) {
  return (assessment?.pages ?? []).flatMap((page) =>
    (page?.elements ?? []).filter((el) => el?.elementType === 'question')
  );
}

/**
 * A sentiment scale renders any number of options, so an oversized answer set is
 * valid. It is still worth flagging: past ten options the decorations start
 * to crowd and the scale gets hard to read on a phone.
 */
function sentimentOptionCount(assessment) {
  return questionElements(assessment)
    .filter((q) => q.type === 'sentiment')
    .flatMap((question) => {
      const count = Object.keys(question.answerSet?.choices ?? {}).length;
      if (count <= MAX_SENTIMENT_OPTIONS) {
        return [];
      }
      return [
        warn(
          question,
          `${count} options is more than the recommended ${MAX_SENTIMENT_OPTIONS} for a sentiment scale`
        ),
      ];
    });
}

const RULES = [sentimentOptionCount];

export function collectWarnings(assessment) {
  if (!assessment) {
    return [];
  }

  return RULES.flatMap((rule) => rule(assessment));
}
